import { Icon } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ValuationItemType } from '~/types/valuationTypes';
import formatTooltipValue from '~valuation/utils/formatTooltipValue';

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatHistoryData(history: any) {
  return Object.keys(history).map((dateString) => {
    const date = new Date(dateString);
    const month = monthNames[date.getMonth()];
    const value = history[dateString] ?? 0;

    return { month, value };
  });
}

const formatNumber = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return `${value}`;
};

const PriceIndex = ({
  valuationData,
}: {
  valuationData: ValuationItemType;
}) => {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const isYearlyChangePercentagePositive =
    valuationData.yearlyChangePercentage > 0;

  const yearlyChangePercentageClassName = isYearlyChangePercentagePositive
    ? 'text-green_light bg-[rgba(12,181,72,0.08)] border-[rgba(12,181,72,0.2)]'
    : 'text-red_dark bg-[rgba(229,12,12,0.08)] border-[rgba(229,12,12,0.2)]';

  return (
    <div className=" flex-1 flex flex-col gap-4x border rounded-[14px] border-gray py-3x px-2x md:px-3x">
      <div className="flex gap-1.5x">
        <div className="w-10 h-10 rounded-[8px] bg-orange_light_2 flex justify-center items-center text-orange">
          <Icon name="up-trend" className="text-orange" size="24px" />
        </div>
        <div className="flex flex-col gap-x">
          <p className="text-body_1 text-gray__dark_2">{t('priceIndex')}</p>
          <p className="text-caption text-gray__dark_1">
            {t('priceIndexTooltipLabel')}
          </p>
        </div>
      </div>
      <div className="h-[186px] w-full">
        <ResponsiveContainer>
          <LineChart
            data={formatHistoryData(valuationData.aggregatedValuationHistory)}
            className="md:pe-2x"
          >
            <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" />

            <XAxis
              dataKey="month"
              tick={{ fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
            />

            <YAxis
              domain={['dataMin - 50000', 'dataMax + 50000']}
              tickFormatter={formatNumber}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280' }}
              tickMargin={locale === 'ar' ? 55 : 0}
            />

            <Tooltip
              formatter={(v) => [formatTooltipValue(v, t), null]}
              labelFormatter={() => ''}
            />

            <Line
              type="monotone"
              dataKey="value"
              stroke="#d4a347"
              strokeWidth={3}
              dot={{
                r: 4,
                strokeWidth: 1,
                fill: '#d4a347',
              }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-center gap-2x">
        <p className="text-body_1 text-gray__dark_2">{t('yearlyChange')}</p>
        <p
          className={`py-1 px-3 text-caption ${yearlyChangePercentageClassName} border rounded-[8px]`}
          dir={valuationData.yearlyChangePercentage >= 0 ? 'rtl' : 'ltr'}
        >
          {valuationData.yearlyChangePercentage}
          {isYearlyChangePercentagePositive ? '%' : ''}
          {isYearlyChangePercentagePositive ? '+' : '%'}
        </p>
      </div>
    </div>
  );
};

export default PriceIndex;
